import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../services/api";
import { useTranslation } from "react-i18next";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import AccountExistsModal from "./AccountExistsModal";
import { PRIVACY_VERSION, TERMS_VERSION } from '../../config/legalVersions';

const GoogleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5 drop-shadow-[0_0_10px_rgba(255,255,255,0.4)]"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M21.35 11.1H12v2.96h5.27c-.23 1.3-.96 2.32-2.05 2.98l3.29 2.55c1.92-1.77 3.04-4.38 3.04-7.5 0-.64-.06-1.26-.2-1.99z"
    />
    <path
      fill="#34A853"
      d="M12.18 22c2.73 0 5.03-.92 6.71-2.49l-3.29-2.55c-.91.62-2.07.99-3.42.99-2.63 0-4.86-1.78-5.67-4.18H3.95v2.6C5.62 19.98 8.64 22 12.18 22z"
    />
    <path
      fill="#FBBC05"
      d="M6.51 13.77a6.3 6.3 0 0 1 0-3.54V7.63H3.95a9.83 9.83 0 0 0 0 8.74l2.56-2.6z"
    />
    <path
      fill="#EA4335"
      d="M12.18 6.86c1.48 0 2.81.49 3.86 1.45l2.88-2.88C17.23 3.54 14.92 2.6 12.18 2.6 8.64 2.6 5.62 4.62 3.95 7.63l2.56 2.6c.81-2.4 3.04-3.37 5.67-3.37z"
    />
  </svg>
);

const RegisterScreen = ({ onSwitchToLogin, onClose, onForgotPassword }) => {
  const { t } = useTranslation();
  // Inline CSS for browser compatibility fallback
  const glassCardStyles = `
    :root {
      --portal-glow-inner: rgba(56, 189, 248, 0.25);
      --portal-glow-mid: rgba(59, 130, 246, 0.15);
      --portal-glow-outer: rgba(191, 219, 254, 0.05);
    }

    .glass-modal-card {
      background-color: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(40px);
      -webkit-backdrop-filter: blur(40px);
      position: relative;
      border-radius: 2.5rem;
      isolation: isolate;
      box-shadow:
        0 30px 60px rgba(0, 0, 0, 0.4),
        inset 0 0 0 1px rgba(255, 255, 255, 0.1);
      transition: all 500ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .glass-modal-card::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: radial-gradient(
        circle at 50% 0%,
        rgba(56, 189, 248, 0.15) 0%,
        transparent 70%
      );
      z-index: -1;
      opacity: 0.8;
    }

    .premium-input {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      transition: all 300ms ease;
    }

    .premium-input:focus {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(56, 189, 248, 0.4);
      box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1);
    }
  `;

  const { register, loginWithGoogle, error, loading, clearError, isNativeApp } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [showAccountExistsModal, setShowAccountExistsModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "" };

    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: "Weak", color: "bg-rose-400/70" };
    if (score <= 3) return { score, label: "Fair", color: "bg-amber-400/70" };
    if (score <= 4) return { score, label: "Good", color: "bg-cyan-400/70" };
    return { score, label: "Strong", color: "bg-emerald-400/70" };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (error) clearError();
    if (localError) setLocalError("");
  };

  const validateForm = () => {
    if (!isChecked) {
      setLocalError("Please agree to the Terms of Service and Privacy Policy");
      return false;
    }
    if (!formData.email) {
      setLocalError("Email is required");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setLocalError("Please enter a valid email address");
      return false;
    }
    if (!formData.password) {
      setLocalError("Password is required");
      return false;
    }
    if (formData.password.length < 6) {
      setLocalError("Password must be at least 6 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const requireConfirm =
      String(process.env.REACT_APP_REQUIRE_EMAIL_CONFIRMATION) === "true";

    if (requireConfirm) {
      try {
        const response = await apiClient.post("/auth/signup", {
          email: formData.email,
          password: formData.password,
        });

        // Set post-registration flag for onboarding
        try {
          localStorage.setItem("show_onboarding_after_verification", "true");
        } catch {}

        setConfirmationSent(true);
        setLocalError("");
      } catch (err) {
        console.error("❌ Registration error:", err);
        console.error("Error response:", err.response?.data);
        const msg =
          err.response?.data?.error ||
          err.message ||
          "Registration failed. Please try again.";

        // Check if user already exists
        if (
          msg.toLowerCase().includes("already") ||
          msg.toLowerCase().includes("exist")
        ) {
          setShowAccountExistsModal(true);
        } else {
          setLocalError(msg);
        }
      }
      return;
    }

    // Confirmation OFF: use auth.register to auto-login
    try {
      // Set post-registration flag for onboarding (even for auto-login)
      try {
        localStorage.setItem("show_onboarding_after_verification", "true");
      } catch {}

      const result = await register(formData.email, formData.password);
      if (result?.success) {
        onClose();
      } else {
        const message =
          result?.error || "Registration failed. Please try again.";
        if (
          message.toLowerCase().includes("already") ||
          message.toLowerCase().includes("exist")
        ) {
          clearError();
          setShowAccountExistsModal(true);
        } else {
          setLocalError(message);
        }
      }
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Registration failed";

      // Check if user already exists
      if (
        msg.toLowerCase().includes("already") ||
        msg.toLowerCase().includes("exist")
      ) {
        clearError();
        setShowAccountExistsModal(true);
      } else {
        setLocalError(msg);
      }
    }
  };

  const handleGoogleRegister = async () => {
    try {
      setLocalError("");
      console.log("🔄 Starting Google OAuth...");

      if (!isChecked) {
        setLocalError(
          "You must agree to the Terms of Service and Privacy Policy to continue."
        );
        return;
      }

      // Show loading state
      const result = await loginWithGoogle();
      if (result?.success) {
        console.log("✅ Google OAuth initiated successfully");
        // The OAuth flow will redirect automatically
      } else if (result?.error) {
        setLocalError(result.error);
      } else {
        setLocalError("Google login failed. Please try again.");
      }
    } catch (error) {
      console.error("Google registration error:", error);
      setLocalError("Google registration failed. Please try again.");
    }
  };

  // If confirmation sent, show dedicated confirmation screen
  if (confirmationSent) {
    return (
      <>
        <style>{glassCardStyles}</style>
        <div className="w-full h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex  items-center justify-center p-4 overflow-hidden">
          <div className="w-full max-w-md">
            {/* Success Icon */}
            <div className="text-center my-8">
              <div className="w-20 h-20 bg-gradient-to-r from-emerald-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail className="w-10 h-10 text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
                Check your email
              </h1>
              <p className="text-cyan-100/80 text-base">
                We sent a verification link to
              </p>
              <p className="text-white font-semibold mt-1">{formData.email}</p>
            </div>

            {/* Instructions */}
            <div className="glass-modal-card relative overflow-visible rounded-2xl shadow-2xl border border-white/20 p-8 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-7 h-7 rounded-full bg-cyan-400/20 border border-cyan-200/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-100 text-sm font-bold">1</span>
                  </div>
                  <p className="text-white/90">
                    Open the email we just sent you
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-7 h-7 rounded-full bg-cyan-400/20 border border-cyan-200/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-100 text-sm font-bold">2</span>
                  </div>
                  <p className="text-white/90">Click the verification link</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-7 h-7 rounded-full bg-cyan-400/20 border border-cyan-200/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-100 text-sm font-bold">3</span>
                  </div>
                  <p className="text-white/90">
                    You'll be redirected back and can sign in
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-white/20">
                <p className="text-white/70 text-sm text-center mb-3">
                  Didn't receive the email? Check your spam folder
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={async () => {
                      try {
                        await apiClient.post("/auth/resend-verification", {
                          email: formData.email,
                        });
                        alert(
                          "Verification email resent! Please check your inbox."
                        );
                      } catch (err) {
                        alert("Failed to resend email. Please try again.");
                      }
                    }}
                    className="flex-1 py-2 px-4 rounded-xl border border-cyan-200/40 bg-cyan-400/20 text-cyan-100 hover:bg-cyan-400/30 hover:border-cyan-200/60 transition-all text-sm font-semibold tracking-wide backdrop-blur-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200"
                  >
                    Resend Email
                  </button>
                  <button
                    onClick={() => setConfirmationSent(false)}
                    className="flex-1 py-2 px-4 rounded-xl border border-white/25 bg-white/10 text-white/90 hover:bg-white/20 hover:border-white/30 transition-all text-sm font-semibold tracking-wide backdrop-blur-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
                  >
                    Try Again
                  </button>
                </div>
              </div>

              <button
                onClick={onSwitchToLogin}
                className="w-full rounded-2xl border border-white/25 bg-gradient-to-br from-cyan-300/25 via-sky-500/20 to-blue-700/30 text-sky-50 py-3 px-4 font-semibold tracking-wide flex items-center justify-center space-x-2 backdrop-blur-2xl shadow-[0_20px_60px_rgba(14,165,233,0.35)] transition-all duration-200 hover:scale-[1.01] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200"
              >
                Go to Sign In
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{glassCardStyles}</style>
      <div className={`w-full min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex ${isNativeApp ? 'items-center' : 'items-center'} justify-center p-4 overflow-hidden`}>
        <div className="w-full max-w-md my-8">
          {/* Header - Premium Apple Style */}
          <div className={`text-center ${isNativeApp ? 'mb-8 mt-12' : 'mb-10'}`}>
            {/* ScrollEd Logo - 90% width with 5% margin each side, max-w-sm on desktop */}
            <div className="mb-10">
              <img
                src="/logo/scrolled-logo.png"
                alt="ScrollEd"
                className="w-[90%] max-w-sm mx-auto drop-shadow-[0_0_40px_rgba(125,211,252,0.5)]"
              />
            </div>
            <p className="text-white/70 text-base font-light tracking-wide">
              {t('auth.create_account_title')}
            </p>
          </div>

          {/* Register Form */}
          <div className="glass-modal-card relative overflow-visible rounded-2xl shadow-2xl border border-white/20 p-8 space-y-6 transition-shadow duration-500">
            {!isNativeApp && (
              <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Display */}
              {(error || localError) && (
                <div className="p-4 rounded-2xl bg-rose-500/15 border border-rose-200/60 text-rose-50/90 backdrop-blur-xl shadow-[0_12px_40px_rgba(225,29,72,0.35)]">
                  <p className="text-sm font-semibold tracking-wide">
                    {error || localError}
                  </p>
                </div>
              )}

              {/* Full Name Field */}
              {/* <div>
                <label className="block text-sm font-semibold text-white/90 mb-2 tracking-wide">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <User className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]" />
                  </div>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/80 focus-visible:border-cyan-100/70 transition-all duration-200 ease-in-out disabled:opacity-60"
                    placeholder="Enter your full name"
                    disabled={loading}
                  />
                </div>
              </div> */}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2 tracking-wide">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Mail className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/80 focus-visible:border-cyan-100/70 transition-all duration-200 ease-in-out disabled:opacity-60"
                    placeholder="Enter your email"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Username Field */}
              {/* <div>
                <label className="block text-sm font-semibold text-white/90 mb-2 tracking-wide">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <span className="text-cyan-100/80 text-base font-semibold drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]">
                      @
                    </span>
                  </div>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/80 focus-visible:border-cyan-100/70 transition-all duration-200 ease-in-out disabled:opacity-60"
                    placeholder="Choose a username"
                    disabled={loading}
                  />
                </div>
              </div> */}

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2 tracking-wide">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Lock className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-14 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/80 focus-visible:border-cyan-100/70 transition-all duration-200 ease-in-out disabled:opacity-60"
                    placeholder="Create a password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 text-cyan-50/80 p-2 hover:text-white hover:bg-white/20 hover:brightness-110 hover:border-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:hover:brightness-100"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white/70 font-medium">
                        Password strength:
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          passwordStrength.score <= 1
                            ? "text-rose-200"
                            : passwordStrength.score <= 3
                            ? "text-amber-200"
                            : passwordStrength.score <= 4
                            ? "text-cyan-200"
                            : "text-emerald-200"
                        }`}
                      >
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-xl border border-white/20">
                      <div
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      Use 10+ characters with mix of letters, numbers & symbols
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-semibold text-white/90 mb-2 tracking-wide">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Lock className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_10px_rgba(56,189,248,0.4)]" />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-14 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/80 focus-visible:border-cyan-100/70 transition-all duration-200 ease-in-out disabled:opacity-60"
                    placeholder="Confirm your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 text-cyan-50/80 p-2 hover:text-white hover:bg-white/20 hover:brightness-110 hover:border-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:hover:brightness-100"
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {formData.confirmPassword && (
                  <div className="mt-2 flex items-center space-x-2">
                    {formData.password === formData.confirmPassword ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-emerald-300 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                        <span className="text-xs text-emerald-200 font-medium">
                          Passwords match
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-rose-300 drop-shadow-[0_0_8px_rgba(251,113,133,0.5)]" />
                        <span className="text-xs text-rose-200 font-medium">
                          Passwords don't match
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Register Button */}
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full overflow-hidden rounded-2xl border border-white/25 bg-gradient-to-br from-cyan-300/25 via-sky-500/20 to-blue-700/30 text-sky-50 py-3 px-4 font-semibold tracking-wide flex items-center justify-center space-x-2 backdrop-blur-2xl shadow-[0_20px_60px_rgba(14,165,233,0.35)] transition-all duration-200 ease-in-out hover:scale-[1.01] hover:shadow-[0_30px_90px_rgba(14,165,233,0.45)] hover:brightness-110 hover:bg-gradient-to-br hover:from-cyan-300/30 hover:via-sky-500/25 hover:to-blue-700/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:brightness-100"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/80 border-t-transparent"></div>
                    <span>{t('common.loading')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('auth.create_account_btn')}</span>
                    <ArrowRight className="w-5 h-5 text-sky-50/90 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </>
                )}
                              </button>
                            </form>
                          )}
              
                          {/* Divider */}
                          {!isNativeApp && (
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-white/20"></div>
                              </div>
                              <div className="relative flex justify-center text-sm">
                                <span className="px-3 py-0.5 rounded-full border border-white/15 bg-white/10 text-gray-100 tracking-[0.25em] uppercase text-[11px]">
                                  {t('auth.or_divider') || 'OR'}
                                </span>
                              </div>
                            </div>
                          )}
              
                          {/* Google Register Button */}
                          <div className={isNativeApp ? 'py-4' : ''}>
                            <button
                              onClick={handleGoogleRegister}
                              disabled={loading}
                              className="w-full rounded-2xl border border-white/25 bg-slate-900/70 text-white/90 py-3 px-4 font-semibold tracking-wide hover:bg-slate-900/80 hover:brightness-110 hover:border-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 transition-all duration-200 ease-in-out flex items-center justify-center space-x-3 shadow-[0_18px_60px_rgba(2,6,23,0.6)] backdrop-blur-2xl"
                            >
                              <GoogleIcon />
                              <span>Continue with Google</span>
                            </button>
                          </div>

                          {/* Legal acceptance applies to both registration methods */}
                          <div className="flex flex-row gap-3 items-start px-1">
                            <input
                              type="checkbox"
                              name="terms"
                              id="terms"
                              checked={isChecked}
                              onChange={() => setIsChecked(!isChecked)}
                              className="w-4 h-4 rounded-lg border-white/20 bg-white/5 text-white focus:ring-0 transition-all cursor-pointer"
                            />
                            <div className="text-[10px] font-bold text-white/60 uppercase tracking-wide leading-4 pt-0.5">
                              <label htmlFor="terms" className="cursor-pointer select-none">
                                By creating an account or continuing with Google, I agree to the{' '}
                              </label>
                              <a
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-legal-version={TERMS_VERSION}
                                className="text-cyan-100 underline underline-offset-2 hover:text-white"
                              >
                                Terms of Service
                              </a>{' '}
                              and acknowledge the{' '}
                              <a
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-legal-version={PRIVACY_VERSION}
                                className="text-cyan-100 underline underline-offset-2 hover:text-white"
                              >
                                Privacy Policy
                              </a>.
                            </div>
                          </div>
              
                          {/* Switch to Login */}
                          {!isNativeApp && (
                            <div className="text-center">
                              <p className="text-gray-100/90">
                                {t('auth.has_account')}{" "}
                                <button
                                  onClick={onSwitchToLogin}
                                  className="inline-flex items-center gap-1 text-sky-200 font-semibold hover:text-white hover:brightness-110 transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200"
                                  disabled={loading}
                                >
                                  {t('auth.sign_in_link')}
                                </button>
                              </p>
                            </div>
                          )}          </div>
        </div>

        {/* Account Already Exists Modal */}
        <AccountExistsModal
          isOpen={showAccountExistsModal}
          onClose={() => setShowAccountExistsModal(false)}
          email={formData.email}
          onSignIn={() => {
            setShowAccountExistsModal(false);
            onSwitchToLogin();
          }}
          onResetPassword={() => {
            setShowAccountExistsModal(false);
            if (onForgotPassword) onForgotPassword();
          }}
          onUseAnotherEmail={() => {
            setShowAccountExistsModal(false);
            setFormData({ ...formData, email: "" });
          }}
        />
      </div>
    </>
  );
};

export default RegisterScreen;
